<script setup lang="ts">
import { fromNow } from '@thecompaniesapi/date-helpers'
import type { Action } from '@thecompaniesapi/shared/client'

const props = defineProps({
  action: {
    required: true,
    type: Object as PropType<Action>,
  },
  column: {
    default: false,
    type: Boolean,
  },
  content: {
    default: true,
    type: Boolean,
  },
  cost: {
    default: true,
    type: Boolean,
  },
  count: {
    default: 0,
    type: Number,
  },
  maxDomains: {
    default: undefined,
    type: Number,
  },
  request: {
    default: false,
    type: Boolean,
  },
  retry: {
    default: false,
    type: Boolean,
  },
  text: {
    default: true,
    type: Boolean,
  },
  times: {
    default: false,
    type: Boolean,
  },
})

const listsStore = useListsStore()
const actionsStore = useActionsStore()
const { t } = useI18n()

const actionList = computed(() => {
  return listsStore.lists.find(list => list.id === props.action.listId)
})

const domains = computed(() => {
  return getDomainsForAction(props.action).slice(0, 4)
})

const query = computed(() => {
  return props.action.data?.query
})

const question = computed(() => {
  return props.action.data?.question
})

async function onRetry() {
  await actionsStore.retryAction({ actionId: props.action.id })
}
</script>

<template>
  <div
    class="select-none"
    :class="{
      'flex w-full flex-col gap-2': column,
      'flex items-center': !column,
    }"
  >
    <div
      class="flex"
      :class="{ 'flex-col gap-2': column, 'items-center gap-1': !column }"
    >
      <span
        v-if="text"
        class="truncate"
      >
        <template v-if="!request">
          {{ t(`actions.jobs.${action.status}.${action.data?.job}`, { list: actionList?.name }) }}
        </template>

        <template v-else>
          {{ t(`actions.jobs.request.${action.data?.job}`, { list: actionList?.name }) }}
        </template>
      </span>

      <div
        class="flex items-center"
        :class="{ 'gap-2': column, 'gap-1': !column }"
      >
        <div
          v-if="query && query?.length"
          class="overflox-x-auto flex flex-wrap items-center gap-1"
        >
          <UTooltip
            v-for="(condition, index) in (column ? query : query.slice(0, 4))"
            :key="`query-${index}`"
            :popper="{ placement: 'bottom' }"
            :text="condition?.values?.join(', ') || t('noValues')"
          >
            <UBadge
              class="cursor-pointer truncate"
              :class="column ? {} : { 'hidden sm:flex': index === 0, 'hidden md:flex': index === 1, 'hidden lg:flex': index === 2, 'hidden xl:flex': index === 3 }"
              size="xs"
              variant="subtle"
            >
              {{ `${t(`datapoints.attributes.${condition.attribute.replaceAll('.', '-')}`)} ${condition.values?.length ? `(${condition.values.length})` : ''}` }}
            </UBadge>
          </UTooltip>
        </div>

        <div
          v-if="question && content"
          class="flex flex-wrap items-center md:max-w-[200px]"
          :class="{ 'gap-2': column, 'gap-1': !column }"
        >
          <UTooltip
            class="w-full"
            :popper="{ placement: 'bottom' }"
            :text="question"
          >
            <UBadge
              class="flex w-full cursor-pointer items-center"
              color="purple"
              size="xs"
              variant="subtle"
            >
              <div class="flex items-center gap-1 overflow-hidden">
                <UIcon
                  class="inline-block size-5 md:hidden"
                  :name="getIcon('ask')"
                />

                <span class="hidden truncate md:inline-block">
                  {{ question }}
                </span>

                <span v-if="count > 1">
                  <UBadge
                    color="emerald"
                    size="xs"
                    variant="subtle"
                  >
                    x{{ count }}
                  </UBadge>
                </span>
              </div>
            </UBadge>
          </UTooltip>
        </div>

        <div
          v-else-if="domains.length && content"
          class="flex flex-wrap items-center"
          :class="{ 'gap-2': column, 'gap-1': !column }"
        >
          <div
            v-for="(domain, index) in domains.slice(0, maxDomains ? maxDomains : domains.length)"
            :key="`action-${domain}`"
            :class="{ 'hidden sm:flex': index === 0, 'hidden md:flex': index === 1, 'hidden lg:flex': index === 2, 'hidden xl:flex': index === 3 }"
          >
            <UBadge
              class="min-w-[48px] overflow-hidden"
              size="xs"
              variant="subtle"
            >
              <span class="truncate">
                {{ domain }}
              </span>
            </UBadge>
          </div>

          <UTooltip
            v-if="action.data?.totalDomains && action.data.totalDomains > 4 && content"
            :popper="{ placement: 'bottom' }"
            :text="t('domains', action.data?.totalDomains)"
          >
            <UBadge
              class="cursor-pointer overflow-hidden truncate"
              size="xs"
              variant="subtle"
            >
              <span class="truncate">
                +{{ action.data?.totalDomains - (maxDomains ? maxDomains : 4) }}
              </span>
            </UBadge>
          </UTooltip>
        </div>

        <UTooltip
          v-if="action.data?.totalDomains && content"
          :popper="{ placement: 'bottom' }"
          :text="t('progress')"
        >
          <UBadge
            class="cursor-pointer whitespace-nowrap"
            :color="action.data?.totalDomains > (action.data?.completedDomains || 0) ? 'blue' : 'green'"
            size="xs"
            variant="subtle"
          >
            <span class="hidden sm:flex">
              {{ `${action.data?.completedDomains || 0} / ${action.data?.totalDomains}` }}
            </span>

            <span class="flex sm:hidden">
              {{ `${action.data?.completedDomains || 0} / ${action.data?.totalDomains}` }} {{ t('domains', action.data?.totalDomains) }}
            </span>
          </UBadge>
        </UTooltip>
      </div>

      <div
        v-if="retry && action.status === 'failed'"
        class="group flex items-center"
        @mousedown="onRetry"
      >
        <UBadge
          class="group-hover:bg-yellow-500/10 group-hover:text-yellow-500"
          color="yellow"
          size="xs"
          variant="subtle"
        >
          <UIcon
            class="size-4"
            :name="getIcon('refresh')"
          />

          <span>
            {{ t('retry') }}
          </span>
        </UBadge>
      </div>
    </div>

    <div
      v-if="action.cost && cost && text"
      class="flex items-center"
      :class="{ 'gap-2': column, 'gap-1': !column }"
    >
      <span v-if="!column">
        {{ t('for') }}
      </span>

      <div>
        <BaseCoin
          class="hidden lg:flex"
          :credits="action.cost || 0"
          :mono="false"
          size="sm"
          truncate
        />

        <BaseCoin
          class="flex lg:hidden"
          :credits="action.cost || 0"
          hide-suffix
          :mono="false"
          size="sm"
        />
      </div>
    </div>

    <div
      v-if="times && text"
      class="flex items-center"
      :class="{ 'gap-2': column, 'gap-1': !column }"
    >
      <UTooltip
        class="hidden sm:flex"
        :popper="{ placement: 'bottom' }"
        :text="t('scheduled')"
      >
        <UBadge
          class="flex cursor-pointer items-center gap-1 truncate"
          size="xs"
          variant="subtle"
        >
          <UIcon
            class="hidden size-4 sm:flex"
            :name="getIcon('play')"
          />

          {{ fromNow(new Date(action.createdAt || '')) }}
        </UBadge>
      </UTooltip>

      <UTooltip
        :popper="{ placement: 'bottom' }"
        :text="t('lastUpdate')"
      >
        <UBadge
          class="flex cursor-pointer items-center gap-1 truncate"
          size="xs"
          variant="subtle"
        >
          <UIcon
            class="size-4"
            :name="getIcon('refresh')"
          />

          {{ fromNow(new Date(action.updatedAt || '')) }}
        </UBadge>
      </UTooltip>
    </div>

    <div
      v-if="request"
      class="flex items-center"
    >
      {{ t('questionMark') }}
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "domain": "domain|domains",
    "for": "for",
    "lastUpdate": "Last update",
    "noValues": "No values",
    "progress": "Progress",
    "questionMark": "?",
    "retry": "Retry",
    "scheduled": "Requested"
  },
  "es": {
    "domain": "dominio|dominios",
    "for": "para",
    "lastUpdate": "Última actualización",
    "noValues": "No hay valores",
    "progress": "Progreso",
    "questionMark": "?",
    "retry": "Reintentar",
    "scheduled": "Solicitado"
  },
  "fr": {
    "domain": "domaine|domaines",
    "for": "pour",
    "lastUpdate": "Dernière mise à jour",
    "noValues": "Aucune valeur",
    "progress": "Progression",
    "questionMark": "?",
    "retry": "Réessayer",
    "scheduled": "Demandé"
  }
}
</i18n>
